import { AccountType, AddressEnumType, AddressType, Beneficiary, EdealDocument, CountryType, PartnerCode, StatusType, DocumentType, DocumentTypeDetail } from "./types";
import { 
    BOURSEDIRECT_PHONENUMBER, 
    DIRECTSECURITIES_PHONENUMBER, 
    MEILLEURTAUX_PHONENUMBER 
} from './constants';
import toast from 'react-hot-toast';
import { RegistrationDataType } from "@src/stores/stores.types";

/**
 * Génére un code aléatoire à 6 chiffres ( utilisé pour le code sms)
 * @returns {string}
 */
export const generateRandomNumber = () => {
    const min = 100000;
    const max = 999999;
    return (Math.floor(Math.random() * (max - min + 1)) + min).toString();
}

/**
 * Format a phone number according to the given country.
 * If the country is France ( phone_extension starts with +33)
 * and the input value starts with 0 and has a length of 10,
 * remove the first 0 and spaces.
 * Otherwise, remove spaces from the input value.
 * @param countryData - the country data
 * @param inputValue - the input value
 * @returns {string} the formatted phone number
 */
export const formattedPhoneNumber = (countryData: CountryType, inputValue: string) => {
    if (!countryData || !countryData.phone_extension || !inputValue) {
        return inputValue;
    }

    return countryData.phone_extension.startsWith('+33') && inputValue.startsWith('0') && inputValue.length === 10 
        ? inputValue.slice(1).split(' ').join('') 
        : inputValue.split(' ').join('');
};

export const extractPhoneNumber = (countryCodeArray: string[], phone: string) => {
    if (!phone) return { countryCode: '', extractPhone: '' };
    let extractPhone = '', countryCode = '';

    countryCodeArray.forEach((element) => {
        if (phone.startsWith(element)) { 
            countryCode = element;
            extractPhone = phone.slice(element.length);
            if (countryCode === '+33') {
                extractPhone = '0' + extractPhone;
            }
            
            return { countryCode, extractPhone };
        }
    });
    return { countryCode, extractPhone };
}

export const formatIban = (value: string) => {
    return value
        .replace(/\s+/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim();
};
export const unformatIban = (value: string) => value.replace(/\s+/g, '');

export const getDocumentStatusColor = (status: StatusType): string => {
    const statusColorMap: Record<StatusType, string> = {
        [StatusType.accepted]: '#00AB6B',
        [StatusType.pending]: '#E59802',
        [StatusType.refused]: '#D80027',
        [StatusType.error]: '#D80027',
    };
    return statusColorMap[status] || '#000000';
};

export const formatDate = (value: string) => {
    const cleanValue = value.replace(/\D+/g, '');
    if (cleanValue.length <= 2) {
        return cleanValue;
    } else if (cleanValue.length <= 4) {
        return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}`;
    } else {
        return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}/${cleanValue.slice(4, 8)}`;
    }
};

export const dateToTimestamp = (dateStr: string) => {
    const [day, month, year] = dateStr.split('/');
    const date = new Date(`${year}-${month}-${day}`);
    return Math.floor(date.getTime() / 1000);
};

export const timestampToDate = (timestamp: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Returns the appropriate phone number based on partner information
 * @param isPartner - Boolean indicating if the registration is from a partner
 * @param partnerCode - The code of the partner (if applicable)
 * @returns {string} The phone number to display
 */
export const getPhoneNumber = (isPartner: boolean, partnerCode?: string): string => {
    if (!isPartner) {
        return BOURSEDIRECT_PHONENUMBER;
    }
    return partnerCode === PartnerCode.CGPAS ? MEILLEURTAUX_PHONENUMBER : DIRECTSECURITIES_PHONENUMBER;
};

/**
 * Handles API errors by displaying a toast error message
 * @param error - The error object from the API response
 * @param customMessage - Custom error message to display (default: 'Une erreur est survenue, veuillez recommencer.')
 * @param position - Position of the toast notification (default: 'top-center')
 * @param duration - Duration of the toast in milliseconds (default: 3000)
 * @returns {void}
 */
export const handleApiError = (
    error: any, 
    customMessage: string = 'Une erreur est survenue, veuillez recommencer.',
    position: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' = 'top-center',
    duration: number = 3000
): void => {
    if (!error.isHandled) {
        toast.error(customMessage, { position, duration });
    }
};

export const isDevOrRe7 = () => {
    const { hostname, href } = window.location;
    return (
        hostname === "localhost" ||
        href.includes(".re7.capitol.fr")
    );
}

/**
 * Dispatches an address to the registration store
 * @param addresses - The addresses array from the registration store
 * @param addressType - The type of the address to update
 * @param addressData - The address data to dispatch
 */
export const dispatchAddressToRegistrationStore = (addresses: AddressType[], addressType: string, addressData: AddressType) => {
    const addressToUpdate = addresses.find((obj) => obj.address_type === addressType);
    if (addressToUpdate) {
        Object.assign(addressToUpdate, {
            street_number: addressData.street_number || '',
            id: addressData.id,
            street_extension: addressData.street_extension || '',
            street_name_further: addressData.street_name_further || '',
            street_name: addressData.street_name || '',
            city: {
                zipcode: addressData.city?.zipcode || '',
                name: addressData.city?.name || '',
            },
            country: addressData.country || '',
        });
    }
};

/**
 * Updates the registration store from a demand
 * @param demandData - The demand data
 * @param registration - The registration data
 * @param setField - The setField function from the registration store
 */
export const updateRegistrationStoreFromDemand = (demandData: any, registration: RegistrationDataType, setField: (fieldPath: string, value: any) => void) => {
    setField('person', {
        ...registration.person,
        basic: {
            ...registration.person.basic,
            ...demandData.person.basic,
            birthname: demandData.person.basic.birthname || demandData.person.basic.lastname,
            residence_country:
                demandData.addresses.find((addr: AddressType) => addr.address_type === AddressEnumType.postale)
                    ?.country || '',
        },
        birth: {
            ...registration.person.birth,
            date: Math.floor(new Date(demandData.person.birth.date).getTime() / 1000),
            place: demandData.person.birth.place,
        },
        legal: demandData.person.legal,
        family: demandData.person.family,
        professional: demandData.person.professional,
    });

    demandData.addresses.forEach((address: AddressType) => dispatchAddressToRegistrationStore(registration.addresses, address.address_type, address));
    demandData.company_addresses.forEach((address: AddressType) => dispatchAddressToRegistrationStore(registration.company_addresses, address.address_type, address));

    if (demandData.accounts.length > 0 && demandData.accounts[0]?.id) {
        setField(
            'accounts',
            demandData.accounts.map(({ type, is_transfer, id, disabled, fees, is_peaj }: AccountType, index: number) => ({
                index,
                title: type === 'ordinaire' ? 'Compte titres ordinaire' : '',
                type,
                is_transfer,
                id,
                disabled: disabled || false,
                fees: fees ? { ...fees } : undefined,
                is_peaj: is_peaj || false,
            }))
        );
    }
    
    if (demandData.company) {
        setField('company', {
            ...demandData.company,
            ibans: demandData.company.ibans ? demandData.company.ibans : [{ iban: '', bic: '', bank_name: '' }],
            beneficial_owners: demandData.company.beneficial_owners
                ? [
                    ...demandData.company.beneficial_owners.filter((b: Beneficiary) => b.data.is_legal_representative),
                    ...demandData.company.beneficial_owners.filter((b: Beneficiary) => !b.data.is_legal_representative)
                ]
                : []
        });
    }
};

export const formatNumberWithSpaces = (value: string | number) => {
    if (!value) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export function checkBeneficiariesDocuments(beneficiaries: Array<{data?: {is_beneficial_owner?: boolean, is_legal_representative?: boolean}, documents?: Array<EdealDocument>}>): boolean {
    return beneficiaries
        .filter((b) => b.data?.is_beneficial_owner === true && b.data?.is_legal_representative === false)
        .some((b) => {
            const docs = b?.documents ?? [];
            const hasIdFront = docs.some((doc: EdealDocument) => doc.type?.name === DocumentType.id && doc.type_detail?.name === DocumentTypeDetail.idcard);
            const hasIdBack = docs.some((doc: EdealDocument) => doc.type?.name === DocumentType.id && doc.type_detail?.name === DocumentTypeDetail.back);
            const hasIdFrontBack = docs.some((doc: EdealDocument) => doc.type?.name === DocumentType.id && doc.type_detail?.name === DocumentTypeDetail.passport);
            const hasAddr = docs.some((doc: EdealDocument) => doc.type?.name === DocumentType.address && doc.type_detail?.name === DocumentTypeDetail.others);
            return !(((hasIdFront && hasIdBack) || hasIdFrontBack) && hasAddr);
        });
}
